import { isoZ, z } from 'my-zod'
import { enumOptionsToObject } from '../../internal'

export const disputeStatusOptions = [
  'lost',
  'needs_response',
  'under_review',
  'warning_closed',
  'warning_needs_response',
  'warning_under_review',
  'won',
  // computed status - original status is 'needs_response' and evidenceDueDate is before now
  'deadline_missed'
] as const

export const disputeStatus = enumOptionsToObject(disputeStatusOptions)

export type DisputeStatus = (typeof disputeStatusOptions)[number]

export const disputeStatusLabels = {
  lost: 'Lost',
  needs_response: 'Needs Response',
  under_review: 'Under Review',
  warning_closed: 'Warning Closed',
  warning_needs_response: 'Warning Needs Response',
  warning_under_review: 'Warning Under Review',
  won: 'Won',
  deadline_missed: 'Deadline Missed'
} as Record<DisputeStatus, string>

export const disputeReasonOptions = [
  'bank_cannot_process',
  'check_returned',
  'credit_not_processed',
  'customer_initiated',
  'debit_not_authorized',
  'duplicate',
  'fraudulent',
  'general',
  'incorrect_account_details',
  'insufficient_funds',
  'product_not_received',
  'product_unacceptable',
  'subscription_canceled',
  'unrecognized'
] as const

export const disputeReason = enumOptionsToObject(disputeReasonOptions)

export const disputeReasonLabels = {
  bank_cannot_process: 'Bank Cannot Process',
  check_returned: 'Check Returned',
  credit_not_processed: 'Credit Not Processed',
  customer_initiated: 'Customer Initiated',
  debit_not_authorized: 'Debit Not Authorized',
  duplicate: 'Duplicate',
  fraudulent: 'Fraudulent Transaction',
  general: 'General',
  incorrect_account_details: 'Incorrect Account Details',
  insufficient_funds: 'Insufficient Funds',
  product_not_received: 'Product Not Received',
  product_unacceptable: 'Product Unacceptable',
  subscription_canceled: 'Subscription Canceled',
  unrecognized: 'Unrecognized'
} as Record<DisputeReason, string>

export type DisputeReason = (typeof disputeReasonOptions)[number]

export const customerOrderDisputesModel = z.object({
  id: z.string(),
  shortId: z.string(),
  amount: z.number(),
  disputedChargeId: z.string(),
  customerOrderId: z.string(),
  evidenceDueDate: isoZ.instant().nullable(),
  evidenceSubmittedDate: isoZ.instant().nullable(),
  reason: z.string(),
  status: z.enum(disputeStatusOptions),
  createdDate: isoZ.instant(),
  closedDate: isoZ.instant().nullable()
})

export type customerOrderDisputesModel = z.infer<typeof customerOrderDisputesModel>

const DisputeFile = z.object({
  id: z.string(),
  filename: z.string(),
  type: z.string(),
  size: z.number()
})

/**
 * List of all stripe dispute evidence in camel case
 * @link https://docs.stripe.com/api/disputes/object#dispute_object-evidence
 */
export const disputeEvidenceModel = z.object({
  accessActivityLog: z.string().nullable(),
  billingAddress: z.string().nullable(),
  cancellationPolicy: DisputeFile.nullable(),
  cancellationPolicyDisclosure: z.string().nullable(),
  cancellationRebuttal: z.string().nullable(),
  customerCommunication: DisputeFile.nullable(),
  customerEmailAddress: z.string().nullable(),
  customerName: z.string().nullable(),
  // customerPurchaseIp: z.string().nullable(),
  customerSignature: DisputeFile.nullable(),
  duplicateChargeDocumentation: DisputeFile.nullable(),
  // duplicateChargeExplanation: z.string().nullable(),
  // duplicateChargeId: z.string().nullable(),
  productDescription: z.string().nullable(),
  // receipt: z.string().nullable(),
  refundPolicy: DisputeFile.nullable(),
  refundPolicyDisclosure: z.string().nullable(),
  refundRefusalExplanation: z.string().nullable(),
  serviceDate: z.string().nullable(),
  serviceDocumentation: DisputeFile.nullable(),
  // shippingAddress: z.string().nullable(),
  // shippingCarrier: z.string().nullable(),
  shippingDate: z.string().nullable(),
  shippingDocumentation: DisputeFile.nullable(),
  shippingTrackingNumber: z.string().nullable(),
  uncategorizedFile: DisputeFile.nullable(),
  uncategorizedText: z.string().nullable()
})

export type DisputeFile = z.infer<typeof DisputeFile>

export const disputeEvidenceOptions = Object.keys(disputeEvidenceModel.shape) as (keyof z.infer<
  typeof disputeEvidenceModel
>)[]

export type DisputeEvidence = (typeof disputeEvidenceOptions)[number]

export const disputeEvidence = enumOptionsToObject(disputeEvidenceOptions)
